import React from 'react';

/**
 * The line icons on the Links page: a page's icon, the app glyphs, and the
 * handful of controls. One stroke style, drawn on a 24-unit grid, coloured by
 * currentColor so each follows the text it sits in - light or dark.
 *
 * A shape is a path string, or { c: [cx, cy, r] } for a circle, or
 * { r: [x, y, w, h, rx] } for a rectangle.
 */
const SHAPES = {
  // Page icons - the names a page may carry (routes/links.js takes any
  // lower-case name; an unknown one is drawn as `link`).
  tools: ['M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z'],
  briefcase: [{ r: [2, 7, 20, 14, 2] }, 'M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16'],
  news: ['M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2',
    'M18 14h-8', 'M15 18h-5', 'M10 6h8v4h-8z'],
  trophy: ['M6 9H4.5a2.5 2.5 0 0 1 0-5H6', 'M18 9h1.5a2.5 2.5 0 0 0 0-5H18', 'M4 22h16',
    'M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22', 'M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22',
    'M18 2H6v7a6 6 0 0 0 12 0V2z'],
  globe: [{ c: [12, 12, 10] }, 'M2 12h20', 'M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z'],
  book: ['M4 19.5A2.5 2.5 0 0 1 6.5 17H20', 'M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z'],
  star: ['M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z'],
  link: ['M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71', 'M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71'],
  folder: ['M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z'],
  chart: ['M18 20V10', 'M12 20V4', 'M6 20v-6'],
  radio: [{ c: [12, 12, 2] }, 'M16.24 7.76a6 6 0 0 1 0 8.49', 'M7.76 16.24a6 6 0 0 1 0-8.49',
    'M19.07 4.93a10 10 0 0 1 0 14.14', 'M4.93 19.07a10 10 0 0 1 0-14.14'],
  sun: [{ c: [12, 12, 4] }, 'M12 2v2', 'M12 20v2', 'M4.93 4.93l1.41 1.41', 'M17.66 17.66l1.41 1.41', 'M2 12h2', 'M20 12h2',
    'M6.34 17.66l-1.41 1.41', 'M19.07 4.93l-1.41 1.41'],
  heart: ['M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z'],
  building: ['M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z', 'M9 22V12h6v10'],
  scale: ['M12 3v18', 'M5 21h14', 'M3 7h18', 'M6 7l-3 7a3 3 0 0 0 6 0z', 'M18 7l-3 7a3 3 0 0 0 6 0z'],
  shield: ['M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z', 'M9 12l2 2 4-4'],
  percent: ['M19 5L5 19', { c: [6.5, 6.5, 2.5] }, { c: [17.5, 17.5, 2.5] }],
  users: ['M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2', { c: [9, 7, 4] }, 'M23 21v-2a4 4 0 0 0-3-3.87', 'M16 3.13a4 4 0 0 1 0 7.75'],
  laptop: [{ r: [4, 4, 16, 11, 1.5] }, 'M2 19h20', 'M9.5 19l.5-1h4l.5 1'],
  calendar: [{ r: [3, 4, 18, 18, 2] }, 'M16 2v4', 'M8 2v4', 'M3 10h18', 'M8 14h.01', 'M12 14h.01', 'M16 14h.01', 'M8 18h.01', 'M12 18h.01'],

  // Mac app glyphs, by scheme - plain line drawings of what the app is for,
  // never Apple's own artwork.
  'x-apple.systempreferences': [{ c: [12, 12, 3] },
    'M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z'],
  shortcuts: ['M12 2l9 5-9 5-9-5z', 'M3 12l9 5 9-5', 'M3 17l9 5 9-5'],
  macappstore: ['M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z', 'M3 6h18', 'M16 10a4 4 0 0 1-8 0'],
  music: ['M9 18V5l12-2v13', { c: [6, 18, 3] }, { c: [18, 16, 3] }],

  // A shortcut tile: what its keys are for, when that is plain from its name,
  // else a command key.
  command: ['M18 3a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3H6a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3V6a3 3 0 0 0-3-3 3 3 0 0 0-3 3 3 3 0 0 0 3 3h12a3 3 0 0 0 3-3 3 3 0 0 0-3-3z'],
  keyboard: [{ r: [2, 5, 20, 14, 2] }, 'M6 9h.01', 'M10 9h.01', 'M14 9h.01', 'M18 9h.01', 'M6 13h.01', 'M18 13h.01', 'M10 13h4', 'M7 16h10'],
  lock: [{ r: [4, 11, 16, 11, 2] }, 'M8 11V7a4 4 0 0 1 8 0v4'],
  preview: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6', { c: [11.5, 14.5, 2.5] }, 'M13.5 16.5L16 19'],
  smile: [{ c: [12, 12, 10] }, 'M8 14s1.5 2 4 2 4-2 4-2', 'M9 9h.01', 'M15 9h.01'],
  mac: [{ r: [2, 3, 20, 14, 2] }, 'M8 21h8', 'M12 17v4'],
  info: [{ c: [12, 12, 10] }, 'M12 16v-4', 'M12 8h.01'],

  // App glyphs, by scheme.
  'ms-calculator': [{ r: [4, 2, 16, 20, 2] }, 'M8 6h8', 'M8 10h.01', 'M12 10h.01', 'M16 10h.01', 'M8 14h.01', 'M12 14h.01',
    'M16 14h.01', 'M8 18h.01', 'M12 18h.01', 'M16 18h.01'],
  'ms-screenclip': [{ c: [6, 6, 3] }, { c: [6, 18, 3] }, 'M20 4L8.12 15.88', 'M14.47 14.48L20 20', 'M8.12 8.12L12 12'],
  'ms-settings': ['M4 21v-7', 'M4 10V3', 'M12 21v-9', 'M12 8V3', 'M20 21v-5', 'M20 12V3', 'M1 14h6', 'M9 8h6', 'M17 16h6'],
  'ms-paint': ['M9.06 11.9l8.07-8.06a2.85 2.85 0 1 1 4.03 4.03l-8.06 8.08',
    'M7.07 14.94c-1.66 0-3 1.35-3 3.02 0 1.33-2.5 1.52-2 2.02 1.08 1.1 2.49 2.02 4 2.02 2.2 0 4-1.8 4-4.04a3.01 3.01 0 0 0-3-3.02z'],
  'ms-clock': [{ c: [12, 12, 10] }, 'M12 6v6l4 2'],
  'ms-photos': [{ r: [3, 3, 18, 18, 2] }, { c: [8.5, 8.5, 1.5] }, 'M21 15l-5-5L5 21'],
  'ms-todo': ['M9 11l3 3L22 4', 'M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11'],
  mailto: ['M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z', 'M22 6l-10 7L2 6'],

  // Controls.
  pencil: ['M12 20h9', 'M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z'],
  trash: ['M3 6h18', 'M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6', 'M10 11v6', 'M14 11v6', 'M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
  plus: ['M12 5v14', 'M5 12h14'],
  external: ['M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6', 'M15 3h6v6', 'M10 14L21 3'],
  search: [{ c: [11, 11, 7] }, 'M21 21l-4.35-4.35'],
  history: ['M3 3v5h5', 'M3.05 13A9 9 0 1 0 6 5.3L3 8', 'M12 7v5l4 2'],
  restore: ['M1 4v6h6', 'M3.51 15a9 9 0 1 0 2.13-9.36L1 10'],
  up: ['M12 19V5', 'M5 12l7-7 7 7'],
  down: ['M12 5v14', 'M19 12l-7 7-7-7'],
  close: ['M18 6L6 18', 'M6 6l12 12'],
  check: ['M20 6L9 17l-5-5'],
  bin: ['M3 6h18', 'M8 6V4h8v2', 'M19 6l-1 14H6L5 6'],
  tab: ['M3 8V6a2 2 0 0 1 2-2h5l2 3h7a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z', 'M12 11v6', 'M9 14h6'],
  window: [{ r: [3, 4, 18, 16, 2] }, 'M3 9h18', 'M6.5 6.5h.01', 'M9 6.5h.01'],
  copy: [{ r: [9, 9, 12, 12, 2] }, 'M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1'],
  windows: [{ r: [3, 3, 8, 8, 1] }, { r: [13, 3, 8, 8, 1] }, { r: [3, 13, 8, 8, 1] }, { r: [13, 13, 8, 8, 1] }],
  // The density toggle (082): roomy rows, and tight ones.
  comfortable: [{ r: [3, 4, 18, 7, 1.5] }, { r: [3, 13, 18, 7, 1.5] }],
  compact: ['M4 5h16', 'M4 10h16', 'M4 15h16', 'M4 20h16'],
  // Jarvis's own doing, in History.
  sparkle: ['M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z', 'M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8z']
};

/** The names a page's icon may be, in the order the picker offers them. */
export const PAGE_ICONS = ['link', 'tools', 'briefcase', 'news', 'radio', 'trophy', 'sun', 'globe', 'book', 'scale',
  'chart', 'building', 'folder', 'star', 'heart', 'shield', 'percent', 'users', 'laptop', 'calendar'];

/**
 * The glyph for a shortcut tile, from the words in its name - Calculator,
 * Screenshot, Lock the screen - or the command key when nothing fits.
 * Only a picture: nothing depends on it.
 */
const SHORTCUT_GLYPHS = [
  [/calculat/i, 'ms-calculator'], [/screen ?shot|snip|capture/i, 'ms-screenclip'], [/lock/i, 'lock'],
  [/setting|preference/i, 'x-apple.systempreferences'], [/preview|pdf/i, 'preview'], [/clock|timer|alarm/i, 'ms-clock'],
  [/photo|picture|image/i, 'ms-photos'], [/emoji/i, 'smile'], [/paint|draw/i, 'ms-paint'], [/remind|to ?do|task/i, 'ms-todo'],
  [/music/i, 'music'], [/mail/i, 'mailto']
];
export const shortcutGlyph = (title) => (SHORTCUT_GLYPHS.find(([re]) => re.test(String(title || ''))) || [null, 'command'])[1];

export function LinkIcon({ name, size = 16, className = '', title = null }) {
  const shapes = SHAPES[name] || SHAPES.link;
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8"
         strokeLinecap="round" strokeLinejoin="round" className={className}
         aria-hidden={title ? undefined : 'true'} role={title ? 'img' : undefined} focusable="false">
      {title && <title>{title}</title>}
      {shapes.map((s, i) => {
        if (typeof s === 'string') return <path key={i} d={s} />;
        if (s.c) return <circle key={i} cx={s.c[0]} cy={s.c[1]} r={s.c[2]} />;
        return <rect key={i} x={s.r[0]} y={s.r[1]} width={s.r[2]} height={s.r[3]} rx={s.r[4]} />;
      })}
    </svg>
  );
}

/** Is there a glyph for this app scheme? */
export const hasGlyph = (scheme) => Boolean(scheme && SHAPES[scheme]);

/** Six dots: the handle a row is dragged by. */
export const Grip = () => (
  <svg viewBox="0 0 10 16" width="10" height="16" aria-hidden="true" focusable="false" fill="currentColor">
    <circle cx="2.5" cy="3" r="1.4" /><circle cx="7.5" cy="3" r="1.4" />
    <circle cx="2.5" cy="8" r="1.4" /><circle cx="7.5" cy="8" r="1.4" />
    <circle cx="2.5" cy="13" r="1.4" /><circle cx="7.5" cy="13" r="1.4" />
  </svg>
);
